import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import {
  Box,
  Card,
  CardContent,
  Button,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Input,
  Textarea,
  Label,
  Alert,
} from '../components/ui'
import { Breadcrumbs } from '../components/Breadcrumbs'
import PageHero from '../components/PageHero'
import './DlaFirm.css'

const DlaFirm: React.FC = () => {
  useSEO({
    title: 'Dla firm — szkolenia i warsztaty zespołowe | wojskaspecjalne.pl',
    description:
      'Szkolenia i warsztaty dla firm: decyzyjność pod presją, bezpieczeństwo, komunikacja i procedury. Program dopasowany do branży, poziomu i celu zespołu.',
    keywords:
      'szkolenia dla firm, warsztaty dla firm, team training, decyzyjność pod presją, bezpieczeństwo, komunikacja, procedury, odporność informacyjna',
    canonicalPath: '/dla-firm',
  })

  const [inquiry, setInquiry] = useState<{
    company: string
    contactName: string
    email: string
    phone: string
    peopleCount: string
    preferredDate: string
    goal: string
    interests: string
    notes: string
  }>({
    company: '',
    contactName: '',
    email: '',
    phone: '',
    peopleCount: '',
    preferredDate: '',
    goal: '',
    interests: 'Szkolenia dla zespołu: decyzyjność pod presją / bezpieczeństwo / komunikacja / procedury / odporność informacyjna',
    notes: '',
  })

  const b2bMailToHref = useMemo(() => {
    const to = 'patryk.korzeniowski@epko.tech'
    const subject = 'Zapytanie B2B — szkolenia dla firm'
    const bodyLines = [
      'Dzień dobry,',
      '',
      'Proszę o propozycję programu i wycenę szkolenia / warsztatu dla zespołu.',
      '',
      `Firma: ${inquiry.company || '-'}`,
      `Osoba kontaktowa: ${inquiry.contactName || '-'}`,
      `E-mail: ${inquiry.email || '-'}`,
      `Telefon: ${inquiry.phone || '-'}`,
      `Liczba osób: ${inquiry.peopleCount || '-'}`,
      `Preferowany termin: ${inquiry.preferredDate || '-'}`,
      `Cel (np. współpraca / decyzyjność / bezpieczeństwo): ${inquiry.goal || '-'}`,
      `Zakres / moduły: ${inquiry.interests || '-'}`,
      `Dodatkowe informacje: ${inquiry.notes || '-'}`,
      '',
      'Pozdrawiam,',
    ]
    const body = encodeURIComponent(bodyLines.join('\n'))
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${body}`
  }, [inquiry])

  const briefText = useMemo(() => {
    return [
      'ZAPYTANIE B2B — SZKOLENIA DLA FIRM',
      `Firma: ${inquiry.company || '-'}`,
      `Osoba kontaktowa: ${inquiry.contactName || '-'}`,
      `E-mail: ${inquiry.email || '-'}`,
      `Telefon: ${inquiry.phone || '-'}`,
      `Liczba osób: ${inquiry.peopleCount || '-'}`,
      `Preferowany termin: ${inquiry.preferredDate || '-'}`,
      `Cel: ${inquiry.goal || '-'}`,
      `Zakres / moduły: ${inquiry.interests || '-'}`,
      `Dodatkowe informacje: ${inquiry.notes || '-'}`,
    ].join('\n')
  }, [inquiry])

  const [copied, setCopied] = useState(false)
  const handleCopyBrief = async () => {
    try {
      await navigator.clipboard.writeText(briefText)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      // Fallback: do nothing (clipboard might be blocked)
      setCopied(false)
    }
  }

  return (
    <Box className="page dla-firm-page premium-page-bg" style={{ minHeight: '100vh' }}>
      <Box className="container premium-page-container">
        <Breadcrumbs
          items={[
            { label: 'Strona główna', path: '/' },
            { label: 'Dla firm' },
          ]}
        />

        <PageHero
          badge="B2B"
          title="Dla firm"
          subtitle="Szkolenia i warsztaty zespołowe — decyzyjność pod presją, bezpieczeństwo, współpraca i procedury. Program szyty na miarę."
        />

        {/* Hero highlight */}
        <Card size="4" className="dla-firm-hero-card fade-in-up">
          <CardContent>
            <Heading as="h2" size="6" className="dla-firm-hero-title">
              Szkolenia dla firm —{' '}
              <Text as="span" className="gradient-text">
                mniej chaosu, więcej decyzji
              </Text>
            </Heading>
            <Text as="p" size="3" className="dla-firm-hero-subtitle">
              Projektujemy warsztaty i treningi zespołowe, które poprawiają współpracę i skuteczność działania pod presją.
              Dopasowujemy poziom do grupy — od podstaw po scenariusze decyzyjne i ćwiczenia procedur.
            </Text>

            <Box display="flex" className="dla-firm-hero-actions">
              <Link to="/dla-firm#szybka-wycena">
                <Button variant="solid" size="3" className="dla-firm-cta-primary">
                  Szybka wycena (60s)
                </Button>
              </Link>
              <Link to="/dla-firm#custom">
                <Button variant="surface" size="3" className="dla-firm-cta-secondary">
                  Zobacz jak układamy program
                </Button>
              </Link>
            </Box>

            <Text as="p" size="2" color="muted" className="dla-firm-hero-microcopy">
              Najszybsza droga: wypełnij brief (60 sekund) — wracamy z propozycją programu i orientacyjną wyceną.
            </Text>

            <ul className="dla-firm-quick-list" role="list" aria-label="Najważniejsze elementy oferty">
              <li className="dla-firm-quick-item">
                <strong>Decyzyjność pod presją</strong> — scenariusze, priorytety, odpowiedzialność
              </li>
              <li className="dla-firm-quick-item">
                <strong>Współpraca i komunikacja</strong> — role, zasady, szybkie domykanie ustaleń
              </li>
              <li className="dla-firm-quick-item">
                <strong>Bezpieczeństwo i procedury</strong> — podejście „zero chaosu”, standardy pracy
              </li>
              <li className="dla-firm-quick-item">
                <strong>Program szyty na miarę</strong> — branża, poziom, cel, czas i logistyka
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Lead capture */}
        <Box as="section" id="szybka-wycena" className="dla-firm-section" aria-labelledby="dla-firm-lead-title">
          <Heading as="h2" size="7" id="dla-firm-lead-title" className="dla-firm-section-title">
            Szybka wycena w 60 sekund
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Wpisz minimum (liczba osób + termin) i kliknij „Wyślij zapytanie”. Im więcej szczegółów, tym trafniejsza propozycja programu.
          </Text>

          <Card size="4" className="fade-in-up">
            <CardContent>
              <Box display="grid" className="dla-firm-form-grid" aria-label="Formularz zapytania B2B">
                <Box className="dla-firm-form-field">
                  <Label>Firma</Label>
                  <Input
                    value={inquiry.company}
                    onChange={(e) => setInquiry((p) => ({ ...p, company: e.target.value }))}
                    placeholder="Nazwa firmy"
                  />
                </Box>
                <Box className="dla-firm-form-field">
                  <Label>Osoba kontaktowa</Label>
                  <Input
                    value={inquiry.contactName}
                    onChange={(e) => setInquiry((p) => ({ ...p, contactName: e.target.value }))}
                    placeholder="Imię i nazwisko"
                  />
                </Box>
                <Box className="dla-firm-form-field">
                  <Label>E-mail</Label>
                  <Input
                    value={inquiry.email}
                    onChange={(e) => setInquiry((p) => ({ ...p, email: e.target.value }))}
                    placeholder="adres@firma.pl"
                    inputMode="email"
                  />
                </Box>
                <Box className="dla-firm-form-field">
                  <Label>Telefon (opcjonalnie)</Label>
                  <Input
                    value={inquiry.phone}
                    onChange={(e) => setInquiry((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="+48…"
                    inputMode="tel"
                  />
                </Box>
                <Box className="dla-firm-form-field">
                  <Label>Liczba osób</Label>
                  <Input
                    value={inquiry.peopleCount}
                    onChange={(e) => setInquiry((p) => ({ ...p, peopleCount: e.target.value }))}
                    placeholder="np. 12"
                    inputMode="numeric"
                  />
                </Box>
                <Box className="dla-firm-form-field">
                  <Label>Preferowany termin</Label>
                  <Input
                    value={inquiry.preferredDate}
                    onChange={(e) => setInquiry((p) => ({ ...p, preferredDate: e.target.value }))}
                    placeholder="np. marzec 2026 / 2 dni / piątek–sobota"
                  />
                </Box>
                <Box className="dla-firm-form-field dla-firm-form-field--full">
                  <Label>Cel szkolenia (opcjonalnie)</Label>
                  <Input
                    value={inquiry.goal}
                    onChange={(e) => setInquiry((p) => ({ ...p, goal: e.target.value }))}
                    placeholder="np. współpraca / leadership / decyzyjność / bezpieczeństwo"
                  />
                </Box>
                <Box className="dla-firm-form-field dla-firm-form-field--full">
                  <Label>Co Was interesuje?</Label>
                  <Textarea
                    value={inquiry.interests}
                    onChange={(e) => setInquiry((p) => ({ ...p, interests: e.target.value }))}
                    rows={3}
                  />
                </Box>
                <Box className="dla-firm-form-field dla-firm-form-field--full">
                  <Label>Dodatkowe informacje</Label>
                  <Textarea
                    value={inquiry.notes}
                    onChange={(e) => setInquiry((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="np. poziom grupy, preferencje (więcej relaksu vs więcej aktywności), ograniczenia czasowe"
                    rows={4}
                  />
                </Box>
              </Box>

              <Box display="flex" className="dla-firm-form-actions">
                <a href={b2bMailToHref} className="dla-firm-form-action-link" aria-label="Wyślij zapytanie e-mailem">
                  <Button variant="solid" size="3">
                    Wyślij zapytanie
                  </Button>
                </a>
                <Button variant="surface" size="3" onClick={handleCopyBrief}>
                  Skopiuj brief
                </Button>
              </Box>

              {copied && (
                <Alert variant="success" className="dla-firm-form-alert">
                  Skopiowano brief do schowka.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Offer - all in one place */}
        <Box as="section" className="dla-firm-section" aria-labelledby="dla-firm-allinone-title">
          <Heading as="h2" size="7" id="dla-firm-allinone-title" className="dla-firm-section-title">
            Program dopasowany do celu zespołu
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Zamiast “ogólników” — konkretne moduły i ćwiczenia, które wspierają realne działania w pracy.
          </Text>

          <Box className="dla-firm-trust-strip" aria-label="Standard realizacji">
            <div className="dla-firm-trust-item">
              <strong>Briefing i zasady</strong>
              <span>uczestnicy wiedzą co i jak</span>
            </div>
            <div className="dla-firm-trust-item">
              <strong>Opieka instruktorska</strong>
              <span>prowadzenie od A do Z</span>
            </div>
            <div className="dla-firm-trust-item">
              <strong>Jedna lokalizacja</strong>
              <span>aktywność + relaks bez logistyki</span>
            </div>
          </Box>

          <Box display="grid" className="premium-grid dla-firm-features-grid">
            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Decyzyjność pod presją
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Scenariusze decyzyjne: priorytety, delegowanie, komunikacja w ograniczeniach czasowych.
                </Text>
                <Link to="/dla-firm#team-building" className="dla-firm-card-link">
                  Zobacz szczegóły →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Komunikacja i współpraca
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Role, odpowiedzialności, zasady decyzji i krótkie rytuały, które poprawiają tempo i jakość pracy.
                </Text>
                <Link to="/dla-firm#custom" className="dla-firm-card-link">
                  Ułóżmy scenariusz →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Bezpieczeństwo i procedury
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Podejście „zero chaosu”: zasady, ryzyka, kontrola i kultura bezpieczeństwa — przydatne także w pracy.
                </Text>
                <Link to="/dla-firm#szkolenia" className="dla-firm-card-link">
                  Zobacz moduły →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Odporność informacyjna (opcjonalnie)
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Narzędzia krytycznego myślenia: rozpoznawanie manipulacji, higiena informacji, jasne kryteria oceny źródeł.
                </Text>
                <Link to="/biblioteka" className="dla-firm-card-link">
                  Poznaj metodykę →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Program pod cel zespołu
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Współpraca, leadership, komunikacja, bezpieczeństwo — dobieramy moduły i ćwiczenia do Twoich założeń.
                </Text>
                <Link to="/dla-firm#custom" className="dla-firm-card-link">
                  Porozmawiajmy →
                </Link>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Realizacja od A do Z
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Briefing, harmonogram, opieka na miejscu i jasne zasady — żeby Wasz zespół mógł po prostu przyjechać i
                  korzystać.
                </Text>
                <Link to="/kontakt" className="dla-firm-card-link">
                  Zapytaj o szczegóły →
                </Link>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Team building */}
        <Box as="section" id="team-building" className="dla-firm-section" aria-labelledby="dla-firm-team-building-title">
          <Heading as="h2" size="7" id="dla-firm-team-building-title" className="dla-firm-section-title">
            Ćwiczenia scenariuszowe (opcja terenowa)
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Scenariusze zespołowe: komunikacja, role i decyzje pod presją. Format dobieramy do poziomu grupy.
          </Text>

          <Box display="grid" className="dla-firm-two-col">
            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Co dostajecie
                </Heading>
                <ul className="dla-firm-bullets" role="list">
                  <li>Briefing bezpieczeństwa i zasady stanowisk</li>
                  <li>Instruktaż + prowadzenie przez cały blok</li>
                  <li>Rywalizacja (ranking) lub zadania zespołowe</li>
                  <li>Podsumowanie: feedback i „after action review”</li>
                </ul>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Dla jakich zespołów
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Zespoły sprzedażowe, IT, produkcja, management — tam, gdzie liczy się komunikacja, koncentracja i
                  odpowiedzialność. Ustalamy „progi wejścia” tak, żeby każdy czuł się komfortowo.
                </Text>
                <Link to="/kontakt" className="dla-firm-card-link">
                  Dobierzmy format →
                </Link>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Trainings */}
        <Box as="section" id="szkolenia" className="dla-firm-section" aria-labelledby="dla-firm-trainings-title">
          <Heading as="h2" size="7" id="dla-firm-trainings-title" className="dla-firm-section-title">
            Szkolenia korporacyjne (warsztaty)
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Moduły warsztatowe: komunikacja, decyzyjność, odporność na stres, procedury i bezpieczeństwo.
          </Text>

          <Box display="grid" className="premium-grid">
            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Komunikacja i współpraca
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Proste narzędzia, które podnoszą jakość współpracy: role, odpowiedzialności, zasady decyzji w zespole.
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Decyzyjność pod presją
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Ćwiczenia scenariuszowe: priorytety, delegowanie, szybkie decyzje i praca w warunkach ograniczeń.
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Bezpieczeństwo i procedury
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Podejście „zero chaosu”: jasne zasady, ryzyka, kontrola i kultura bezpieczeństwa — przydatne także w pracy.
                </Text>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Formats */}
        <Box as="section" id="formaty" className="dla-firm-section" aria-labelledby="dla-firm-formats-title">
          <Heading as="h2" size="7" id="dla-firm-formats-title" className="dla-firm-section-title">
            Formaty realizacji
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Dobieramy format do celu i kalendarza zespołu — od krótkich warsztatów po intensywny dzień scenariuszowy.
          </Text>

          <Box display="grid" className="premium-grid">
            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Warsztat 3–4h (najczęstszy start)
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Konkretny moduł: komunikacja, decyzyjność, procedury lub odporność informacyjna. Dużo ćwiczeń, mało teorii.
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  1 dzień (scenariusze + praktyka)
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Praca na scenariuszach, role w zespole, priorytety i komunikacja pod presją — z podsumowaniem i wdrożeniami.
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  2 dni (utrwalenie i transfer do pracy)
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Dzień 1: intensywny trening. Dzień 2: dopracowanie procedur i „jak to przenieść do codziennej pracy”.
                </Text>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Custom */}
        <Box as="section" id="custom" className="dla-firm-section" aria-labelledby="dla-firm-custom-title">
          <Heading as="h2" size="7" id="dla-firm-custom-title" className="dla-firm-section-title">
            Custom program — dopasujemy szkolenie do Twojej firmy
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Ustalamy cele, branżę, poziom zespołu i ograniczenia. Składamy program z modułów i ćwiczeń, żeby efekt był mierzalny.
          </Text>

          <Box display="grid" className="premium-grid">
            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Pakiet „Decyzyjność + Procedury”
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Scenariusze decyzyjne + uporządkowanie zasad pracy: priorytety, delegowanie, kontrola ryzyk.
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Pakiet „Współpraca + Komunikacja”
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Role w zespole, domykanie ustaleń, feedback i „after action review” — praktycznie, na ćwiczeniach.
                </Text>
              </CardContent>
            </Card>

            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  Pakiet „Odporność informacyjna”
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Higiena informacji, rozpoznawanie manipulacji, jasne kryteria oceny źródeł i decyzje oparte o dane.
                </Text>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Process */}
        <Box as="section" className="dla-firm-section" aria-labelledby="dla-firm-process-title">
          <Heading as="h2" size="7" id="dla-firm-process-title" className="dla-firm-section-title">
            Jak to działa
          </Heading>
          <Text as="p" size="3" className="dla-firm-section-subtitle">
            Szybko, konkretnie i bez zaskoczeń. Ustalamy potrzeby, proponujemy program, ustalamy realizację.
          </Text>

          <Box display="grid" className="premium-grid">
            <Card size="4" className="fade-in-up">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  1) Krótki brief
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Liczba osób, cel, preferowany termin, kontekst (branża) i ograniczenia.
                </Text>
              </CardContent>
            </Card>
            <Card size="4" className="fade-in-up stagger-1">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  2) Propozycja programu
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Składamy moduły i ćwiczenia + plan wdrożeń po szkoleniu.
                </Text>
              </CardContent>
            </Card>
            <Card size="4" className="fade-in-up stagger-2">
              <CardContent>
                <Heading as="h3" size="5" className="dla-firm-card-title">
                  3) Rezerwacja i logistyka
                </Heading>
                <Text as="p" size="3" className="dla-firm-card-text">
                  Ustalamy szczegóły, potwierdzamy termin i przygotowujemy instrukcje dla uczestników.
                </Text>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* FAQ */}
        <Box as="section" className="dla-firm-section" aria-labelledby="dla-firm-faq-title">
          <Heading as="h2" size="7" id="dla-firm-faq-title" className="dla-firm-section-title">
            FAQ
          </Heading>
          <Card size="4" className="fade-in-up">
            <CardContent>
              <Accordion type="single" collapsible>
                <AccordionItem value="faq-1">
                  <AccordionTrigger>Ile osób może wziąć udział?</AccordionTrigger>
                  <AccordionContent>
                    <Text as="p" size="3" color="muted">
                      Dobieramy format pod wielkość zespołu. Napisz ile osób planujesz i czy wolisz jedną grupę czy rotacje
                      w blokach.
                    </Text>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-2">
                  <AccordionTrigger>Czy to jest bezpieczne dla początkujących?</AccordionTrigger>
                  <AccordionContent>
                    <Text as="p" size="3" color="muted">
                      Tak — wszystko zaczyna się od briefingu i zasad. Prowadzimy uczestników krok po kroku i dopasowujemy
                      intensywność do grupy.
                    </Text>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-3">
                  <AccordionTrigger>Jaki format wybrać (3–4h / 1 dzień / 2 dni)?</AccordionTrigger>
                  <AccordionContent>
                    <Text as="p" size="3" color="muted">
                      Najczęściej zaczynamy od warsztatu 3–4h (jeden moduł). Jeśli chcesz ćwiczeń scenariuszowych i wdrożeń,
                      rekomendujemy 1 dzień lub 2 dni. Doradzimy po briefie.
                    </Text>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-4">
                  <AccordionTrigger>Czy możemy połączyć kilka modułów w jeden program?</AccordionTrigger>
                  <AccordionContent>
                    <Text as="p" size="3" color="muted">
                      Tak — składamy program z modułów (np. komunikacja + decyzyjność + procedury) i dopasowujemy ćwiczenia do
                      branży oraz realnych sytuacji w Twojej firmie.
                    </Text>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="faq-5">
                  <AccordionTrigger>Jak wygląda wycena?</AccordionTrigger>
                  <AccordionContent>
                    <Text as="p" size="3" color="muted">
                      Cena zależy od liczby osób, czasu trwania, zakresu aktywności i wybranego planu dnia. Najszybciej:
                      wyślij zapytanie przez kontakt.
                    </Text>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </Box>

        {/* Final CTA */}
        <Card size="4" className="dla-firm-final-cta fade-in-up">
          <CardContent>
            <Heading as="h2" size="6" className="dla-firm-final-cta-title">
              Chcesz gotową propozycję programu?
            </Heading>
            <Text as="p" size="3" className="dla-firm-final-cta-text">
              Napisz liczbę osób, preferowany termin i cel (np. współpraca / decyzyjność / bezpieczeństwo).
              Wrócimy z konkretną propozycją szkolenia.
            </Text>
            <Box display="flex" className="dla-firm-final-cta-actions">
              <Link to="/kontakt">
                <Button variant="solid" size="3">
                  Skontaktuj się
                </Button>
              </Link>
              <Link to="/dla-firm#team-building">
                <Button variant="surface" size="3">
                  Zobacz team building
                </Button>
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default DlaFirm


